import clsx from 'clsx'
import { MousePointerClick, Package, ShoppingCart, TrendingUp, Trophy } from 'lucide-react'
import type { ActivityEvent, DashboardResponse, RankingEntry } from '@/types'
import { useVendorAuth } from '@/auth/VendorAuthProvider'
import { ErrorNote, LoadingScreen } from '@/components/ui'
import { api } from '@/lib/api'
import { currency, timeAgoFromDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'

const MEDAL = ['text-gold', 'text-silver', 'text-bronze']

function RankingRow({ entry }: { entry: RankingEntry }) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 rounded-xl px-3 py-2.5',
        entry.isCurrentUser ? 'accent-bg-soft' : 'bg-surface-sunken'
      )}
    >
      <span className="flex w-6 justify-center">
        {entry.position <= 3 ? (
          <Trophy className={clsx('h-5 w-5', MEDAL[entry.position - 1])} />
        ) : (
          <span className="text-sm font-bold text-ink-muted">{entry.position}</span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">
          {entry.fullName}
          {entry.isCurrentUser && <span className="ml-1 text-xs accent-text">(vos)</span>}
        </p>
        <p className="text-[11px] text-ink-muted">V-{entry.vendorNumber}</p>
      </div>
      <span className="text-sm font-bold text-ink">{currency(entry.salesAmount)}</span>
    </div>
  )
}

const ACTIVITY_ICON = {
  order: ShoppingCart,
  click: MousePointerClick,
  abandoned: Package,
} as const

function ActivityRow({ event }: { event: ActivityEvent }) {
  const Icon = ACTIVITY_ICON[event.type]
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{event.label}</p>
        <p className="truncate text-xs text-ink-muted">{event.detail}</p>
      </div>
      <span className="shrink-0 text-[11px] text-ink-faint">{timeAgoFromDate(event.at)}</span>
    </div>
  )
}

export function Dashboard() {
  const { vendor } = useVendorAuth()
  const { data, loading, error, reload } = useAsync(
    () => api.get<DashboardResponse>('/dashboard'),
    []
  )

  if (loading) return <LoadingScreen label="Cargando tu rendimiento…" />
  if (error || !data) return <ErrorNote message={error?.message ?? 'No se pudo cargar el dashboard.'} onRetry={reload} />

  const { metrics, ranking, activity, currentUserRank } = data

  return (
    <div className="space-y-6 p-4">
      <section>
        <h2 className="mb-1 text-lg font-bold text-ink">Hola, {vendor?.fullName.split(' ')[0]} 👋</h2>
        <p className="text-sm text-ink-muted">Tu rendimiento de este mes</p>
      </section>

      <section className="grid grid-cols-3 gap-3" data-tour="metrics">
        <div className="stat-card">
          <TrendingUp className="h-4 w-4 accent-text" />
          <span className="stat-value text-lg">{currency(metrics.salesAmount)}</span>
          <span className="stat-label">Ventas</span>
        </div>
        <div className="stat-card">
          <ShoppingCart className="h-4 w-4 accent-text" />
          <span className="stat-value">{metrics.closedOrders}</span>
          <span className="stat-label">Pedidos</span>
        </div>
        <div className="stat-card">
          <MousePointerClick className="h-4 w-4 accent-text" />
          <span className="stat-value">{metrics.linkClicks}</span>
          <span className="stat-label">Clics</span>
        </div>
      </section>

      <section data-tour="ranking">
        <h3 className="mb-2 font-semibold text-ink">Ranking de tu sucursal</h3>
        <div className="space-y-2">
          {ranking.length === 0 && <p className="text-sm text-ink-muted">Todavía no hay ventas este mes.</p>}
          {ranking.map((entry) => (
            <RankingRow key={entry.vendorId} entry={entry} />
          ))}
          {currentUserRank && (
            <>
              <p className="pt-1 text-center text-xs text-ink-faint">···</p>
              <RankingRow entry={currentUserRank} />
            </>
          )}
        </div>
      </section>

      {activity.length > 0 && (
        <section>
          <h3 className="mb-1 font-semibold text-ink">Actividad reciente</h3>
          <div className="card divide-y divide-line px-3">
            {activity.map((event, i) => (
              <ActivityRow key={i} event={event} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
